import {useCallback, useContext, useState} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getAccountDomains} from 'src/openapi/admin/apis/GetAccountDomainsApi';
import {updateAccountDomains} from 'src/openapi/admin/apis/UpdateAccountDomainsApi';
import {validateAccountDomain} from 'src/openapi/admin/apis/ValidateAccountDomainApi';
import {useAppDispatch} from 'src/store/hooks';

import {useValidateDomains} from './useValidateDomains';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {CommonHookReturn} from 'src/typedef';

type Props = {
    accountId: number,
};

type UseDomains = ({accountId}: Props) => CommonHookReturn<Array<string>, void> & {
    busyDomains: Record<string, boolean>,
    setLastEditedDomain: (idx: number) => void,
};

export const useDomains: UseDomains = ({accountId}) => {
    const apiDispatch = useAppDispatch();

    const [busyDomains, setBusyDomains] = useState<Record<string, boolean>>({});
    const [lastEditedDomain, setLastEditedDomain] = useState(-1);

    const {validationErrors, removeError} = useContext<ValidationContextType>(ValidationContext);
    const {
        validateDefaultDomain,
        validateDomainError,
        validateDuplicatedDomain,
        validateUnfilledDomain,
    } = useValidateDomains();

    const getDispatchAction = useCallback(async () => {
        const loadDomainsResponse = await apiDispatch(getAccountDomains({accountId}));
        if (loadDomainsResponse.payload.success && !loadDomainsResponse.payload.data.length) {
            const payload: {success: true, error: false, data: string[]} = {
                success: true,
                error: false,
                data: [''],
            };

            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: loadDomainsResponse.payload});
        }
    }, [accountId, apiDispatch]);

    const editValidation = useCallback(async (domains: string[]) => {
        if (!validateDefaultDomain(domains)) {
            if (lastEditedDomain >= 0) {
                const domain = domains[lastEditedDomain];
                if (!domain) {
                    removeError(`account-domain-${lastEditedDomain}`);
                    return;
                } else {
                    removeError(`account-domain-${lastEditedDomain}`, ['DOMAIN_REQUIRED']);
                }

                validateDuplicatedDomain({domains, lastEditedDomain, domain});

                setBusyDomains(bDomains => ({...bDomains, [lastEditedDomain]: true}));
                const validationResponse = await apiDispatch(validateAccountDomain({domain, accountId}));

                if (validationResponse.payload.error) {
                    validateDomainError({
                        error: validationResponse.payload.data,
                        lastEditedDomain,
                    });
                }


                setBusyDomains(({[lastEditedDomain]: validatedDomain, ...bDomains}) => bDomains);
            }
            setLastEditedDomain(-1);
        }
    }, [
        lastEditedDomain,
        removeError,
        accountId,
        apiDispatch,
        validateDefaultDomain,
        validateDomainError,
        validateDuplicatedDomain,
    ]);

    const saveValidation = useCallback((domains: string[]) => {
        let valid = true;

        if (validationErrors.length) {
            valid = false;
        }

        if (
            !validateDefaultDomain(domains) &&
            domains.some((range, id) => !validateUnfilledDomain(range, id))
        ) {
            valid = false;
        }

        return valid;
    }, [validationErrors, validateDefaultDomain, validateUnfilledDomain]);

    const saveDispatchAction = useCallback((requestBody: string[]) => {
        return apiDispatch(updateAccountDomains({accountId, requestBody: requestBody.filter(e => e)}));
    }, [accountId, apiDispatch]);

    return {
        ...useCommonDataHook({
            initValue: [],
            getDispatchAction,
            saveDispatchAction,
            editValidation,
            saveValidation,
        }),
        setLastEditedDomain,
        busyDomains,
    };
};
